import React, { useCallback } from 'react';
import get from 'lodash/get';

import { convertPreVisitFormData } from '../../../Surveys/utils/preVisitForm';
import { TODO_TYPES, APPOINTMENT_CHANNEL_ID } from '../../../../constants';
import getToDoAssigmentText from '../../../../utils/getToDoAssigmentText';
import getSpecification from '../../../../utils/survey/getSpecification';
import { ConsentListItem } from '../VisitConsents/ConsentListItem';
import SurveyListItem from '../../../Surveys/SurveyListItem';
import ProgramPopup from '../../../common/ProgramPopup';
import { timeToShow } from '../../utils/handleTaskTime';
import InvitationListItem from '../InvitationListItem';
import CardIcon from '../../../common/CardIcon';
import ProgramListItem from '../ProgramListItem';
import { isCompleted } from '../../utils';
import { ElectronicConsentListItem } from '../VisitConsents/ElectronicConsentListItem';

export function useTodoRenderItem(formatMessage, setActiveItem) {
  const renderSurvey = useCallback(item => {
    const { description, name, status, heading, date } = getSpecification(
      item,
      formatMessage,
    );
    const hidden = get(item, 'hidden');
    const itemAssignmentId = get(item, 'assignmentId');
    const userAppointment = get(item, 'userAppointment');
    const list = convertPreVisitFormData(formatMessage, userAppointment);
    const completed = isCompleted(get(item, 'todoStatus'));

    const renderSurveyItem = hidden ? null : (
      <SurveyListItem
        key={itemAssignmentId}
        heading={heading}
        title={name}
        status={!completed ? status : null}
        description={description}
        onClick={() => (!completed ? setActiveItem(item) : null)}
        list={list}
        date={date}
      />
    );

    return (
      <If condition={completed}>
        {/* for completed survey popup */}
        <ProgramPopup
          date={get(item, 'completedAt')}
          program={get(item, 'program')}
          openedAssignmentId={itemAssignmentId}
          openedSurveyId={get(item, 'survey.id')}
          activeChannelId={APPOINTMENT_CHANNEL_ID}
          forceFetch
          pdfExportable
        >
          {renderSurveyItem}
        </ProgramPopup>
        <Else />
        {renderSurveyItem}
      </If>
    );
  }, []);

  const renderCarePlan = useCallback(item => {
    const {
      id,
      name,
      expiredTime,
      dueDescription,
      completedTime,
      assigner: { displayableName },
    } = item;
    const subtitle = formatMessage(
      { id: 'carePlan.assignedBy.provider' },
      { displayableName },
    );

    return (
      <CardIcon
        key={id}
        icon="assigned-task"
        title={name}
        subtitle={subtitle}
        onClick={() => setActiveItem(item)}
        withBg
        info={timeToShow(
          completedTime,
          expiredTime,
          dueDescription,
          formatMessage,
        )}
      />
    );
  }, []);

  const renderInvitation = useCallback(item => {
    return (
      <InvitationListItem
        formatMessage={formatMessage}
        invitation={item}
        onClick={() => setActiveItem(item)}
      />
    );
  }, []);

  const renderConsent = useCallback(item => {
    const { todoStatus } = item;

    return (
      <ConsentListItem
        formatMessage={formatMessage}
        entity={item}
        onClick={isCompleted(todoStatus) ? () => setActiveItem(item) : null}
      />
    );
  }, []);

  const renderTask = useCallback(item => {
    const todoTask = get(item, 'todoTask');
    const { name, dueDescription } = todoTask || item;
    const { expiredTime, completedTime } = item;

    return (
      <CardIcon
        icon="assigned-task"
        title={name}
        subtitle={getToDoAssigmentText(item, formatMessage)}
        onClick={() => setActiveItem(item)}
        withBg
        info={timeToShow(
          completedTime,
          expiredTime,
          dueDescription,
          formatMessage,
        )}
      />
    );
  }, []);

  const renderProgram = useCallback(
    item => (
      <ProgramListItem
        entity={item}
        formatMessage={formatMessage}
        onClick={() => setActiveItem(item)}
      />
    ),
    [],
  );

  const renderConsentUserAssignment = useCallback(item => {
    return (
      <ElectronicConsentListItem
        formatMessage={formatMessage}
        entity={item}
        onClick={() => setActiveItem(item)}
      />
    );
  }, []);

  const getRenderByType = useCallback(type => {
    switch (type) {
      case TODO_TYPES.INVITATION:
        return renderInvitation;
      case TODO_TYPES.SURVEY:
        return renderSurvey;
      case TODO_TYPES.CARE_PLAN_TASK:
        return renderCarePlan;
      case TODO_TYPES.CONSENT:
        return renderConsent;
      case TODO_TYPES.TASK:
      case TODO_TYPES.TASK_CUSTOM:
        return renderTask;
      case TODO_TYPES.PROGRAM:
        return renderProgram;
      case TODO_TYPES.CONSENT_USER_ASSIGNMENT:
        return renderConsentUserAssignment;
      default:
        return () => {};
    }
  }, []);

  return {
    getRenderByType,
  };
}



// WEBPACK FOOTER //
// ./src/components/Healthboard/Components/TodoList/useTodoRender.jsx